import {classObserver, React, Fragment, observer, jsondiff} from '@api'
import {Model, IDraggable, TabNode, Action, TabSetNode, I18nLabel, BorderNode, FlexLayoutNode, Rect, CLASSES} from '@flexlayout'
import { ui } from '@stores'
import {action, autorun, runInAction} from 'mobx'
import {disposeOnUnmount} from 'mobx-react'
import {useEffect} from 'react'
import {useUnmount} from 'react-use'
import {TabSets, Tabs, Borders, FloatingWindows, Splitters, LayoutStore, LayoutMetricsElement} from './'

export interface ILayoutProps {
  realtimeResize?: boolean | undefined
  className?: string
  model: Model;
  factory: (node: TabNode) => React.ReactNode;
  font?: IFontValues;
  fontFamily?: string;
  iconFactory?: (node: TabNode) => React.ReactNode | undefined;
  titleFactory?: (node: TabNode) => ITitleObject | React.ReactNode | undefined;
  closeIcon?: React.ReactNode;
  icons?: IIcons;
  onAction?: (action: Action) => Action | undefined;
  onRenderTab?: (
    node: TabNode,
    renderValues: ITabRenderValues
  ) => void;
  onRenderTabSet?: (
    tabSetNode: TabSetNode | BorderNode,
    renderValues: ITabSetRenderValues
  ) => void;
  onModelChange?: (model: Model) => void;
  onExternalDrag?: (event: React.DragEvent<HTMLDivElement>) => undefined | {
    dragText: string,
    json: any,
    onDrop?: (node?: FlexLayoutNode, event?: Event) => void
  };
  classNameMapper?: (defaultClassName: string) => string;
  i18nMapper?: (id: I18nLabel, param?: string) => string | undefined;
  supportsPopout?: boolean | undefined;
  popoutURL?: string | undefined;
}

export interface IFontValues {
  size?: string;
  family?: string;
  style?: string;
  weight?: string;
}

export interface ITabSetRenderValues {
  headerContent?: React.ReactNode;
  stickyButtons: React.ReactNode[];
  buttons: React.ReactNode[];
  headerButtons: React.ReactNode[];
}

export interface ITabRenderValues {
  leading: React.ReactNode;
  content: React.ReactNode;
  name: string;
  buttons: React.ReactNode[];
}

export interface ITitleObject {
  titleContent: React.ReactNode;
  name: string;
}

export interface ILayoutState {
  rect: Rect;
  calculatedHeaderBarSize: number;
  calculatedTabBarSize: number;
  calculatedBorderBarSize: number;
  editingTab?: TabNode;
}

export interface IIcons {
  close?: React.ReactNode;
  popout?: React.ReactNode;
  maximize?: React.ReactNode;
  restore?: React.ReactNode;
  more?: React.ReactNode;
}

/** @hidden @internal */
export interface ILayoutCallbacks {
  i18nName(id: I18nLabel, param?: string): string;

  maximize(tabsetNode: TabSetNode): void;

  realtimeResize?: boolean | undefined
  popoutURL: string;

  supportsPopout: boolean;

  currentDocument?: HTMLDocument | undefined;

  getClassName(defaultClassName: string): string;

  doAction(action: Action): FlexLayoutNode | undefined;

  get domRect(): any;

  get rootDiv(): HTMLDivElement;

  dragStart(
    event: Event | React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement> | React.DragEvent<HTMLDivElement> | undefined,
    dragDivText: string,
    node: FlexLayoutNode & IDraggable,
    allowDrag: boolean,
    onClick?: (event: Event) => void,
    onDoubleClick?: (event: Event) => void
  ): void;

  customizeTab(
    tabNode: TabNode,
    renderValues: ITabRenderValues
  ): void;

  customizeTabSet(
    tabSetNode: TabSetNode | BorderNode,
    renderValues: ITabSetRenderValues
  ): void;

  styleFont: (style: Record<string, string>) => Record<string, string>;

  set editingTab(tabNode: TabNode)

  get editingTab(): TabNode | undefined;
}

export const LayoutStoreContext = React.createContext<LayoutStore>(null)
export const useLayoutStore = () => React.useContext(LayoutStoreContext)

/**
 * A React component that hosts a multi-tabbed layout
 */
@classObserver
export class Layout extends React.Component<ILayoutProps, ILayoutState> {
  store: LayoutStore

  constructor(props: ILayoutProps) {
    super(props)
    this.store = new LayoutStore(this)
    this.store.props = this.props
  }

  UNSAFE_componentWillMount() {
    this.store.registerEffects()
  }

  /** @hidden @internal */
  componentDidMount() {
    const {store} = this

    this.props.model._setPointerFine(window && window.matchMedia && window.matchMedia('(pointer: fine)').matches)

    runInAction(() => {
      store.updateRect()
      store.updateLayoutMetrics()

      // need to re-render if size changes
      store.currentDocument = (store.selfRef.current as HTMLDivElement).ownerDocument
      store.currentWindow = store.currentDocument.defaultView!
      store.resizeObserver = new ResizeObserver(entries => {
        // console.log(entries)
        this.onResized(entries)
      })
      store.resizeObserver.observe(store.selfRef.current!)
    })
  }

  onResized = _.throttle((entries: ResizeObserverEntry[]) => {
    const {store} = this
    store.updateRect(entries[0].contentRect)
  }, ui.settings.flexLayout.throttleResizeMs)

  /** @hidden @internal */
  componentDidUpdate(prevProps, prevState) {
    const {store} = this

    if (this.props.model !== store.previousModel) {
      runInAction(() => {
        store.updateLayoutMetrics()

        if (store.previousModel !== undefined) {
          store.previousModel._setChangeListener(undefined) // stop listening to old model
        }

        store.hasDoneLayoutPass = false
        store.props.model = this.props.model
        this.props.model._setChangeListener(store.onModelChange)
        this.props.model.layout = this
        store.previousModel = this.props.model

        // console.log("Layout time: " + this.layoutTime + "ms Render time: " + (Date.now() - this.start) + "ms");
      })
    }
  }

  componentWillUnmount() {
    // console.log(`unmountING Layout...`)

    this.store.dispose()
  }

  /** @hidden @internal */
  render() {
    const {store, store: {selfRef, getClassName}} = this
    // first render will be used to find the size (via selfRef)
    if (store.firstRender) {
      return (
        <LayoutStoreContext.Provider value={store}>
          <div ref={selfRef} className={getClassName(CLASSES.FLEXLAYOUT__LAYOUT)}>
            <LayoutMetricsElement/>
          </div>
        </LayoutStoreContext.Provider>
      )
    }

    store.asyncDoLayout()

    // this.layoutTime = (Date.now() - this.start);

    const {className, props: {onExternalDrag}, onDragEnter} = store

    return (
      <LayoutStoreContext.Provider value={store}>
        <div ref={selfRef}
             className={className}
             data-last-action={store.model.lastAction}
             onDragEnter={onExternalDrag ? onDragEnter : undefined}
        >
          {store.centerRect ? <Fragment>
            <TabSets/>
            <Tabs/>
            <Borders/>
            <Splitters/>
            <FloatingWindows/>
          </Fragment> : <div data-no-center-rect={true}/>}
          <LayoutMetricsElement/>
        </div>
      </LayoutStoreContext.Provider>
    )
  }
}




// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/view/Layout/Layout.tsx